import { createFileRoute } from '@tanstack/react-router';
import { ArrowLeftRight, Check, X } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';

import {
    PhoebeAgentMark,
    SlackSourceIcon,
} from '@apps/web/components/phoebe_chat_view/brand_marks';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';

const TOKEN_STORAGE_KEY = 'phoebe.slack-agent-v3.link-token';
const LINK_PATH = '/slack-agent-v3/link';

// States the link API returns.
type ApiLinkState =
    | 'active'
    | 'linked'
    | 'expired'
    | 'consumed_by_self'
    | 'consumed_by_other'
    | 'linked_to_other_slack_user'
    | 'invalid';

type LinkState = ApiLinkState | 'loading' | 'connecting' | 'error';

type LinkResponse = {
    readonly state: ApiLinkState;
    readonly workspace_id?: string | null;
};

export function captureLinkToken(): string | null {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const fragmentToken = fragment.get('token');
    window.history.replaceState(null, '', LINK_PATH);
    if (fragmentToken) {
        return fragmentToken;
    }
    const restored = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
    window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    return restored;
}

export function linkSignInUrl(): string {
    return `/sign-in?return_to=${encodeURIComponent(LINK_PATH)}`;
}

export function slackAppUrl(workspaceId: string | null): string {
    return workspaceId
        ? `slack://open?team=${encodeURIComponent(workspaceId)}`
        : 'slack://open';
}

export const Route = createFileRoute('/slack-agent-v3/link')({
    head: () => ({ meta: [{ title: 'Connect Slack | Phoebe' }] }),
    beforeLoad: ({ context }) => {
        const token = captureLinkToken();
        if (!context.sessionState?.session) {
            if (token) {
                window.sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
            }
            window.location.assign(linkSignInUrl());
        } else {
            window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        }
        return { slackLinkToken: token };
    },
    component: SlackAgentLinkPage,
});

async function postLink(
    path: 'inspect' | 'confirm',
    token: string,
    csrfToken: string,
): Promise<LinkResponse> {
    const response = await fetch(
        `${getRuntimeConfig().apiUrl}/integrations/user/slack-agent-v3/link/${path}`,
        {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify({ token }),
        },
    );
    if (!response.ok) {
        throw new Error(path);
    }
    return (await response.json()) as LinkResponse;
}

function SlackAgentLinkPage() {
    const { slackLinkToken, user } = Route.useRouteContext();
    const [state, setState] = useState<LinkState>('loading');
    const [csrfToken, setCsrfToken] = useState<string | null>(null);
    // Only inspect returns the workspace; confirm keeps it for the Slack link.
    const [workspaceId, setWorkspaceId] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const headingRef = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
        if (!slackLinkToken) {
            setState('invalid');
            return;
        }
        setState('loading');
        void (async () => {
            try {
                const csrfResponse = await fetch(
                    `${getRuntimeConfig().apiUrl}/integrations/user/slack-agent-v3/link/csrf`,
                    { credentials: 'include' },
                );
                if (!csrfResponse.ok) {
                    throw new Error('csrf');
                }
                const csrf = (await csrfResponse.json()) as {
                    csrf_token: string;
                };
                setCsrfToken(csrf.csrf_token);
                const result = await postLink(
                    'inspect',
                    slackLinkToken,
                    csrf.csrf_token,
                );
                setWorkspaceId(result.workspace_id ?? null);
                setState(result.state);
            } catch {
                setState('error');
            }
        })();
    }, [slackLinkToken, attempt]);

    // Move focus to the new heading so screen readers announce the result
    // and keyboard users are not left on a button that no longer exists.
    useEffect(() => {
        headingRef.current?.focus({ preventScroll: true });
    }, [state]);

    async function confirm() {
        if (!slackLinkToken || !csrfToken) {
            return;
        }
        setState('connecting');
        try {
            const result = await postLink('confirm', slackLinkToken, csrfToken);
            setState(result.state);
        } catch {
            setState('error');
        }
    }

    const accountName =
        [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
        user?.email ||
        null;
    const view = STATE_VIEWS[state];
    const actionButtons: Record<LinkAction, ReactNode> = {
        connect: (
            <Button
                size="lg"
                className="w-full sm:w-auto"
                status={state === 'connecting' ? 'pending' : 'idle'}
                onClick={() => void confirm()}
            >
                Connect accounts
            </Button>
        ),
        retry: (
            <Button
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => setAttempt((value) => value + 1)}
            >
                Try again
            </Button>
        ),
        openSlack: <a href={slackAppUrl(workspaceId)}>Open Slack</a>,
        goToPhoebe: <a href="/">Go to Phoebe</a>,
        signOut: <a href="/sign-out">Sign out</a>,
    };
    // The last action is the primary one; on narrow screens it sits on top.
    const actions = view.actions.map((action, index) => {
        const button = actionButtons[action];
        if (action === 'connect' || action === 'retry') {
            return <Fragment key={action}>{button}</Fragment>;
        }
        return (
            <Button
                key={action}
                asChild={true}
                size="lg"
                variant={
                    index === view.actions.length - 1 ? 'default' : 'outline'
                }
                className="w-full sm:w-auto"
            >
                {button}
            </Button>
        );
    });
    const showAccount = view.showAccount && accountName !== null;

    return (
        // Anchored near the top, not centered, so the card grows downward
        // between states instead of jumping.
        <main className="bg-secondary/50 flex min-h-dvh justify-center px-4 pt-[12dvh] pb-8 antialiased">
            <Card
                className="h-fit w-full max-w-md gap-0 py-0"
                aria-busy={state === 'loading' || state === 'connecting'}
            >
                <div className="flex flex-col items-center px-6 pt-8 pb-6 text-center sm:px-8">
                    <ConnectionMark tone={view.tone} />
                    <div aria-live="polite" className="mt-6">
                        <h1
                            ref={headingRef}
                            tabIndex={-1}
                            className="text-xl font-semibold tracking-tight text-balance outline-none"
                        >
                            {view.title}
                        </h1>
                        <p className="text-muted-foreground mt-2 text-sm leading-6 text-pretty">
                            {view.description}
                        </p>
                    </div>
                    {showAccount && (
                        <p className="ph-no-capture bg-secondary/60 text-muted-foreground mt-5 w-full rounded-md px-4 py-3 text-sm">
                            Signed in to Phoebe as{' '}
                            <span className="text-foreground font-medium break-all">
                                {accountName}
                            </span>
                            {user?.email && accountName !== user.email && (
                                <span className="block break-all">
                                    {user.email}
                                </span>
                            )}
                        </p>
                    )}
                </div>
                {actions.length > 0 && (
                    <div className="flex flex-col-reverse gap-2 border-t px-6 py-4 sm:flex-row sm:justify-end sm:px-8">
                        {actions}
                    </div>
                )}
            </Card>
        </main>
    );
}

type Tone = 'pending' | 'ready' | 'done' | 'problem';

type LinkAction = 'connect' | 'retry' | 'openSlack' | 'goToPhoebe' | 'signOut';

type LinkView = {
    readonly tone: Tone;
    readonly title: string;
    readonly description: string;
    readonly showAccount: boolean;
    // Rendered in order; the last one is the primary action.
    readonly actions: ReadonlyArray<LinkAction>;
};

const CONNECTED_VIEW: LinkView = {
    tone: 'done',
    title: 'Slack is connected',
    description:
        'You can now mention @Phoebe in any Slack channel it is in, or send it a direct message.',
    showAccount: false,
    actions: ['goToPhoebe', 'openSlack'],
};

const STATE_VIEWS: Record<LinkState, LinkView> = {
    loading: {
        tone: 'pending',
        title: 'Checking your link',
        description: 'This takes a moment.',
        showAccount: false,
        actions: [],
    },
    active: {
        tone: 'ready',
        title: 'Connect Slack to Phoebe',
        description:
            'Connect the Slack account that messaged @Phoebe to your Phoebe account. You only need to do this once.',
        showAccount: true,
        actions: ['connect'],
    },
    connecting: {
        tone: 'pending',
        title: 'Connecting your accounts',
        description:
            'Connect the Slack account that messaged @Phoebe to your Phoebe account. You only need to do this once.',
        showAccount: true,
        actions: ['connect'],
    },
    linked: CONNECTED_VIEW,
    consumed_by_self: CONNECTED_VIEW,
    expired: {
        tone: 'problem',
        title: 'This link has expired',
        description:
            'Links work for 30 minutes. Message @Phoebe in Slack again and it will send you a new link.',
        showAccount: false,
        actions: ['openSlack'],
    },
    // The Slack account is now bound to the other Phoebe account, and Phoebe
    // sends no new link to a bound Slack user, so only an admin can fix it.
    consumed_by_other: {
        tone: 'problem',
        title: 'This link was already used',
        description:
            'A different Phoebe account used this link, so this Slack account is connected to that account. If that was not you, ask your Phoebe admin for help.',
        showAccount: true,
        actions: ['goToPhoebe'],
    },
    // The link stays active, and Phoebe sends no new link while one is
    // active, so the user must reopen this same link as the right account.
    linked_to_other_slack_user: {
        tone: 'problem',
        title: 'Your account uses a different Slack account',
        description:
            'This Phoebe account is already connected to another Slack account. Sign out, sign in to the Phoebe account for this Slack user, then open the same link in Slack again. It works for 30 minutes after Phoebe sent it.',
        showAccount: true,
        actions: ['openSlack', 'signOut'],
    },
    // Unknown or replaced tokens, and non-members on confirm, all return
    // invalid. A newer link may still be active, and Phoebe sends no new link
    // while one is, so point at the latest link instead of a new message.
    invalid: {
        tone: 'problem',
        title: 'This link is not valid',
        description:
            'The link may be incomplete or replaced by a newer one. Open the most recent link Phoebe sent you in Slack. If you still see this page, ask your Phoebe admin to check that you are a member of the organization.',
        showAccount: true,
        actions: ['openSlack'],
    },
    error: {
        tone: 'problem',
        title: 'Phoebe could not check this link',
        description:
            'Something went wrong with the connection or on our side. Check your connection and try again.',
        showAccount: false,
        actions: ['retry'],
    },
};

// Phoebe and Slack joined by a line; the node on the line shows the state.
// Fixed sizes keep the header the same height in every state.
function ConnectionMark({ tone }: { readonly tone: Tone }) {
    const lineClass = cn(
        'h-px w-10 transition-colors duration-200 motion-reduce:transition-none sm:w-14',
        tone === 'done' ? 'bg-emerald-500' : 'bg-border',
    );
    return (
        <div className="flex items-center" aria-hidden="true">
            <BrandTile>
                <PhoebeAgentMark className="size-7" />
            </BrandTile>
            <span className={lineClass} />
            <span
                className={cn(
                    'flex size-7 items-center justify-center rounded-full border transition-colors duration-200 motion-reduce:transition-none',
                    tone === 'pending' && 'bg-card text-muted-foreground',
                    tone === 'ready' && 'bg-card text-foreground',
                    tone === 'done' &&
                        'border-emerald-600 bg-emerald-600 text-white',
                    tone === 'problem' &&
                        'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300',
                )}
            >
                {tone === 'pending' && (
                    <Spinner size="sm" className="motion-reduce:animate-none" />
                )}
                {tone === 'ready' && <ArrowLeftRight className="size-3.5" />}
                {tone === 'done' && (
                    <Check className="size-4" strokeWidth={2.5} />
                )}
                {tone === 'problem' && (
                    <X className="size-3.5" strokeWidth={2.5} />
                )}
            </span>
            <span className={lineClass} />
            <BrandTile>
                <SlackSourceIcon className="size-6" />
            </BrandTile>
        </div>
    );
}

function BrandTile({ children }: { readonly children: ReactNode }) {
    return (
        <span className="bg-background flex size-12 items-center justify-center rounded-lg shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.1)]">
            {children}
        </span>
    );
}
